import experiment from 'experiment';
import constants from '#packages/constants';
import * as stateManagement from '#packages/stateManagement';
import {
  type ColorName,
  isNewColorPaletteOpen,
  unwrapColors,
} from '#packages/theme';
import colorPickerConstants from '../constants';
import type {
  ColorPickerPanelOwnProps,
  ColorPickerPanelStateProps,
  ColorPickerPanelDispatchProps,
  ColorPickerValue,
  ColorPickerPanelOverrideProps,
} from './types';
import type {
  MapStateToProps,
  MapDispatchToProps,
  ThunkAction,
} from 'types/redux';
import type { ColorPalette } from 'types/documentServices';
import _ from 'lodash';
import type { MediaManagerImage } from '#packages/mediaServices';

const stylableEditorActions = stateManagement.stylableEditor.actions;

const MAX_THEME_COLORS_QUANTITY = 9;

export const mapStateToProps: MapStateToProps<
  ColorPickerPanelStateProps,
  ColorPickerPanelOwnProps & ColorPickerPanelOverrideProps
> = ({ editorAPI }, props) => {
  const compRef = editorAPI.selection.getSelectedComponents()[0];
  const componentsSelectors = stateManagement.components.selectors;
  const supportNewColorPalette =
    props.supportNewColorPalette !== undefined
      ? props.supportNewColorPalette
      : isNewColorPaletteOpen(editorAPI);
  const unwrappedValue =
    typeof props.value === 'string' ? unwrapColors(props.value) : props.value;

  // If accent color was replaced and removed,
  // color picker should show the color it was linked to
  const updatedValue =
    editorAPI.theme.colors.getReplacedAccentColor(
      unwrappedValue as ColorName,
    ) ?? unwrappedValue;

  const handleAddThemeColor = async (color: string) => {
    if (props.handleAddThemeColor) {
      return props.handleAddThemeColor(color);
    }
    const palette = editorAPI.theme.colors.getAll();
    const emptyAccentColorKey = _.findKey(palette, (_, key) =>
      editorAPI.theme.colors.isAccentColorEmpty(key as ColorName),
    );

    await editorAPI.theme.colors.update({
      [emptyAccentColorKey]: color,
    });

    return emptyAccentColorKey;
  };

  return {
    formatPreview: props.formatPreview,
    hideAdvancedSettings: props.hideAdvancedSettings,
    withHeaderDivider: props.withHeaderDivider,
    // needed to hide the plus for adding new theme colors in the color picker
    maxThemeColorsQuantity:
      props.maxThemeColorsQuantity ?? MAX_THEME_COLORS_QUANTITY,
    applyOnEnter: props.applyOnEnter,
    shouldExtendDefaultShortcuts: experiment.isOpen('se_colorPickerUndo'),
    keyboardShortcuts: experiment.isOpen('se_colorPickerUndo') && {
      'ctrl + z, command + z': () => editorAPI.history.undo(),
      'ctrl + shift + z, command + shift + z': () => editorAPI.history.redo(),
    },
    componentId: compRef?.id || '',
    componentType:
      componentsSelectors.getCompType(compRef, editorAPI.dsRead) || '',
    msid: editorAPI.dsRead.generalInfo.getMetaSiteId(),
    editorViewMode: editorAPI.viewMode.get(),
    colorResolver: props.colorResolver ?? editorAPI.theme.colors.get,
    focusedPageId: editorAPI.pages.getFocusedPageId(),
    isDraft: editorAPI.dsRead.generalInfo.isDraft(),
    isZoomOut: editorAPI.zoomMode.isInZoomMode(),
    viewerName: editorAPI.dsRead.env.viewer.getViewerName(),
    openThemeManager: props.openThemeManager,
    supportNewColorPalette,
    updatedValue,
    onChange: props.onChange,
    /**
     * The order of the color swatches in the custom color panel,
     * ONE DOES NOT SIMPLY CHANGE THE ORDER OF COLORS
     * 1st - white and black (initial values injected in colorPickerPanel internally)
     * 2nd - passed value
     * 3rd - show the user saved colors
     * 4th and beyond - show specific components colors.
     * @returns {string[]}
     */
    getCustomColors(value?: string): string[] {
      if (props.getCustomColors) {
        return props.getCustomColors(value);
      }
      const {
        customUserAddedSessionColors = [],
        userCustomAddedSiteColorsTemplateMigration = [],
        userCustomAddedSiteColors = [],
        customCompsAddedColors = [],
        customInstanceColors = [],
      } = props;
      const colors = [
        ...new Set([
          ...customUserAddedSessionColors,
          ...userCustomAddedSiteColorsTemplateMigration,
          ...userCustomAddedSiteColors,
          ...customCompsAddedColors,
          ...customInstanceColors,
        ]),
      ].filter(Boolean);

      if (value && !colors.includes(value)) {
        colors.unshift(value);
      }

      return colors;
    },
    handleAddCustomColor: (newCustomColor: string) => {
      if (props.shouldNotAddCustomColor) return;
      if (props.handleAddCustomColor) {
        props.handleAddCustomColor(newCustomColor);
        props.stylableEditorSetUserColors?.();
        props.onChange?.(newCustomColor, { isHover: false, isMouseOut: false });
        return;
      }
      const customColorsAddedByUser = [
        ...(props.userCustomAddedSiteColors || []),
      ];
      customColorsAddedByUser.unshift(newCustomColor);
      props.setUserCustomAddedSiteColors(customColorsAddedByUser);
      props.stylableEditorSetUserColors?.();
      props.onChange?.(newCustomColor, { isHover: false, isMouseOut: false });
    },
    handleAddGradient(newGradientColor) {
      const customGradientsAdded = [...(props.customAddedGradients || [])];
      customGradientsAdded.unshift(newGradientColor);
      props.setCustomAddedGradients(customGradientsAdded);
    },
    handleAddMesh(newMesh) {
      const customMeshAdded = [...(props.customAddedMeshes || [])];
      customMeshAdded.unshift(newMesh);
      props.setCustomAddedMeshes(customMeshAdded);
    },
    handleAddThemeColor,
    getThemeColors() {
      if (props.getThemeColors) {
        return props.getThemeColors();
      }

      const themeColorsMap = supportNewColorPalette
        ? editorAPI.theme.colors.getVisibleThemeColors()
        : editorAPI.theme.colors.getAll('onlyOld');
      const themeColors = Object.keys(themeColorsMap);

      const mappedColors = themeColors
        .filter((item) => item !== props.hiddenColors)
        .map((item) => ({
          id: item,
          value: themeColorsMap[item as keyof ColorPalette],
        }));

      if (supportNewColorPalette) {
        // the colors should be arranged in a "gradient" order (designers request)
        return [
          mappedColors[0],
          ...mappedColors.slice(2, 5),
          mappedColors[1],
          ...mappedColors.slice(5),
        ];
      }
      return mappedColors;
    },
    getColorDefaults() {
      const themeColorsMap = editorAPI.theme.colors.getAdvancedColors();
      const themeColors = Object.keys(themeColorsMap);

      return themeColors.map((item) => ({
        id: item,
        value: themeColorsMap[item as keyof ColorPalette],
      }));
    },
    getLinkedThemeColorFromColorDefault(colorName: string) {
      if (props.getLinkedThemeColorFromColorDefault) {
        return props.getLinkedThemeColorFromColorDefault(colorName);
      }

      return editorAPI.theme.colors.getLinkedThemeColorFromColorDefault(
        colorName,
      );
    },
    addColorHistoryEntry(value: ColorPickerValue) {
      const isNewValue = JSON.stringify(value) !== JSON.stringify(props.value);
      if (props.enableHistory && isNewValue) {
        const entryValue = getEntryValue(value);
        editorAPI.history.add(`new color chosen - ${entryValue}`);
      }
    },
    loadImage: () =>
      new Promise((resolve) => {
        const { mediaManager } = editorAPI.mediaServices;
        mediaManager.open(mediaManager.categories.IMAGE, {
          multiple: false,
          callback: ([item]: MediaManagerImage[] = []) => {
            if (!item) {
              resolve(undefined);
              return;
            }
            const { uri, width, height } = item;
            resolve({ url: uri, width, height });
          },
        });
      }),
    openColorDefaultsHelp: () =>
      editorAPI.panelManager.openHelpCenter(
        colorPickerConstants.COLOR_DEFAULTS_HELP_ID,
      ),
  };
};

function getEntryValue(value: ColorPickerValue) {
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    return 'Mesh';
  }
  return 'Gradient';
}

const openDesignPanelView =
  (view: string): ThunkAction =>
  (dispatch, _, { editorAPI }) => {
    const isLightboxMode = editorAPI.dsRead.pages.popupPages.isPopupOpened();
    if (isLightboxMode) {
      editorAPI.pages.popupPages.close();
    }

    dispatch(
      stateManagement.panels.actions.openLeftPanel(
        constants.ROOT_COMPS.LEFTBAR.DESIGN_PANEL_NAME,
        {
          selectedView: view,
          origin: 'ColorPicker',
        },
      ),
    );
  };

export const mapDispatchToProps: MapDispatchToProps<
  ColorPickerPanelDispatchProps,
  ColorPickerPanelOwnProps
> = {
  stylableEditorSetUserColors: stylableEditorActions.setUserColors,
  openDesignPanelColorView: () =>
    openDesignPanelView(constants.DESIGN_PANEL.VIEWS.COLOR),
  openDesignPanelAdvancedSettingsView: () =>
    openDesignPanelView(constants.DESIGN_PANEL.VIEWS.ADVANCED_SETTINGS),
};
